'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { useRouter, useSearchParams } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import useSWR from 'swr'
import { InviteHeroBadge } from '@/components/common/InviteHeroBadge'
import { InviteBadge } from '../common/InviteBadge'
import { getTrackingFromCookie } from '@/lib/getTrackingFromCookie' 
import { motion } from 'framer-motion'
import { AnimatePresence } from 'framer-motion'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import { API_CONFIG } from '@/config/api'
import { getImageCandidates } from '@/lib/image'

export function detectNanpCountry(e164: string): 'US' | 'CA' | 'PR' | null {
  if (!e164.startsWith('+1')) return null

  const phone = parsePhoneNumberFromString(e164)

  if (!phone) return null

  const country = phone.country // <-- magia real aquí

  if (country === 'US' || country === 'CA' || country === 'PR') {
    return country
  }

  return null
}


const fetcher = (url: string) => fetch(url, { cache: 'no-store' }).then(r => r.json())

type AuthSignupVariant = 'inline' | 'modal'

type AuthSignupChrome = 'glass' | 'none'

export type AuthSignupCardProps = {
  sellerId?: number
  variant?: AuthSignupVariant
  chrome?: AuthSignupChrome
  companyStatus?: number
  companyJoinMode?: 'open' | 'invite_only'
  isWhitelabel?: boolean
  communityName?: string
  communityAvatarUrl?: string | null
  theme?: 'light' | 'dark'
  next?: string
  onRequestLogin?: () => void
  onSuccess?: () => void
}

function CommunityWelcomeHero({
  communityName,
  communityAvatarUrl,
  className,
}: {
  communityName: string
  communityAvatarUrl?: string | null
  className?: string
}) {
  const initials = (communityName || 'C')
    .trim()
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  const avatarCandidates = React.useMemo(
    () =>
      getImageCandidates(communityAvatarUrl, {
        width: 96,
        height: 96,
        siteOrigin: API_CONFIG.BASE_URL,
        assetBaseUrl: process.env.NEXT_PUBLIC_ASSET_BASE_URL,
      }),
    [communityAvatarUrl]
  )

  const [avatarIndex, setAvatarIndex] = React.useState(0)

  React.useEffect(() => {
    setAvatarIndex(0)
  }, [communityAvatarUrl])

  const resolvedAvatarSrc = avatarCandidates[avatarIndex] || ''

  return (
    <div className={cn('text-center', className)}>
      <div className="flex items-center justify-center">
        <div className="h-24 w-24 rounded-full bg-background ring-4 ring-background shadow-sm">
          <div className="h-24 w-24 overflow-hidden rounded-full border border-border ring-2 ring-sky-400/35">
            {resolvedAvatarSrc ? (
              <img
                src={resolvedAvatarSrc}
                alt=""
                className="h-full w-full object-cover"
                onError={() => {
                  setAvatarIndex((prev) =>
                    prev < avatarCandidates.length - 1 ? prev + 1 : prev
                  )
                }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-lg font-semibold">
                {initials}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 text-2xl font-semibold tracking-tight">
        Bienvenido a {communityName}
      </div>
      <div className="mt-1 text-sm text-muted-foreground">
        Crea tu cuenta para continuar
      </div>
    </div>
  )
}

function isGmailAddress(email: string) {
  const e = normalizeEmail(email)
  return e.endsWith('@gmail.com') || e.endsWith('@googlemail.com')
}

const SKIP_OTP_GMAIL_UI =
  typeof process !== 'undefined' &&
  process.env.NEXT_PUBLIC_SKIP_OTP_GMAIL === '1'


function normalizeEmail(s: string) {
  return (s || '').trim().toLowerCase()
}

function clampDigits(s: string, max: number) {
  return String(s || '').replace(/\D/g, '').slice(0, max)
}

type CountryItem = { iso2: string; name: string; calling: string }

const COUNTRIES: CountryItem[] = [
  // 🇲🇽 LATAM
  { iso2: 'MX', name: 'México', calling: '52' },
  { iso2: 'AR', name: 'Argentina', calling: '54' },
  { iso2: 'CL', name: 'Chile', calling: '56' },
  { iso2: 'CO', name: 'Colombia', calling: '57' },
  { iso2: 'PE', name: 'Perú', calling: '51' },
  { iso2: 'EC', name: 'Ecuador', calling: '593' },
  { iso2: 'UY', name: 'Uruguay', calling: '598' },
  { iso2: 'PY', name: 'Paraguay', calling: '595' },
  { iso2: 'BO', name: 'Bolivia', calling: '591' },
  { iso2: 'VE', name: 'Venezuela', calling: '58' },
  { iso2: 'CR', name: 'Costa Rica', calling: '506' },
  { iso2: 'PA', name: 'Panamá', calling: '507' },
  { iso2: 'GT', name: 'Guatemala', calling: '502' },
  { iso2: 'SV', name: 'El Salvador', calling: '503' },
  { iso2: 'HN', name: 'Honduras', calling: '504' },
  { iso2: 'NI', name: 'Nicaragua', calling: '505' },
  { iso2: 'DO', name: 'República Dominicana', calling: '1' },

  // 🇺🇸 Norteamérica
  { iso2: 'US', name: 'United States', calling: '1' },
  { iso2: 'CA', name: 'Canada', calling: '1' },

  // 🇪🇺 Europa clave
  { iso2: 'ES', name: 'España', calling: '34' },
  { iso2: 'FR', name: 'Francia', calling: '33' },
  { iso2: 'IT', name: 'Italia', calling: '39' },
  { iso2: 'DE', name: 'Alemania', calling: '49' },
  { iso2: 'GB', name: 'Reino Unido', calling: '44' },
  { iso2: 'PT', name: 'Portugal', calling: '351' },

  // 🌎 Extra estratégicos
  { iso2: 'BR', name: 'Brasil', calling: '55' },
  { iso2: 'AU', name: 'Australia', calling: '61' },
  { iso2: 'NZ', name: 'New Zealand', calling: '64' },
  { iso2: 'IN', name: 'India', calling: '91' },
  { iso2: 'PH', name: 'Filipinas', calling: '63' },
]

const byIso = Object.fromEntries(COUNTRIES.map(c => [c.iso2, c])) as Record<string, CountryItem>

function normalizeToE164(raw: string) {
  const digits = raw.replace(/[^\d+]/g, '')
  return digits.startsWith('+') ? '+' + digits.slice(1).replace(/\D/g, '') : '+' + digits.replace(/\D/g, '')
}

function byCalling(code: string) {
  // match exact calling code
  return COUNTRIES.find(c => c.calling === code) || null
}

/** OTP boxes (6 rectángulos) */
export function OtpBoxes({
  value,
  onChange,
  length = 6,
  disabled,
  theme = 'dark',
}: {
  value: string
  onChange: (v: string) => void
  length?: number
  disabled?: boolean
  theme?: 'light' | 'dark'
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([])

  function focus(i: number) {
    refs.current[i]?.focus()
    refs.current[i]?.select()
  }

  function setAt(i: number, ch: string) {
    const arr = Array.from({ length }).map((_, idx) => value[idx] ?? '')
    arr[i] = ch
    onChange(arr.join('').replace(/\D/g, '').slice(0, length))
  }

  return (
    <div className="w-full flex justify-center">
      <div className="grid grid-cols-6 gap-2 justify-items-center">
        {Array.from({ length }).map((_, i) => {
          const digit = value[i] ?? ''

          return (
            <div key={i} className="relative">
              <input
                ref={(el) => {
                  refs.current[i] = el
                }}
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                pattern="\d*"
                disabled={disabled}
                value={digit}
                onChange={(e) => {
                  const d = e.target.value.replace(/\D/g, '').slice(-1)
                  setAt(i, d)
                  if (d && i < length - 1) focus(i + 1)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace') {
                    e.preventDefault()
                    if (value[i]) {
                      setAt(i, '')
                    } else if (i > 0) {
                      focus(i - 1)
                      setAt(i - 1, '')
                    }
                  }
                  if (e.key === 'ArrowLeft' && i > 0) focus(i - 1)
                  if (e.key === 'ArrowRight' && i < length - 1) focus(i + 1)
                }}
                onPaste={(e) => {
                  e.preventDefault()
                  const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
                  if (!pasted) return
                  onChange(pasted)
                  const nextFocus = Math.min(pasted.length, length - 1)
                  setTimeout(() => focus(nextFocus), 0)
                }}
                className={cn(
                  // tamaño + centrado del dígito
                  'h-12 w-12 text-center text-lg font-semibold tracking-widest',
                  'rounded-xl border transition outline-none',
                  'focus:ring-2 focus:ring-offset-0',
                  theme === 'dark'
                    ? 'bg-white/5 border-white/10 focus:ring-white/20 text-white'
                    : 'bg-black/5 border-black/10 focus:ring-black/10 text-black',
                  disabled && 'opacity-60 cursor-not-allowed'
                )}
              />

              {/* Animación del dígito: overlay para que "rebote" cada vez que cambia */}
              <div className="pointer-events-none absolute inset-0 grid place-items-center">
                <AnimatePresence mode="popLayout">
                  {digit ? (
                    <motion.span
                      key={`${i}-${digit}`} // <- clave para re-animar por dígito
                      initial={{ y: -6, scale: 0.9, opacity: 0 }}
                      animate={{ y: 0, scale: 1, opacity: 1 }}
                      exit={{ y: 6, scale: 0.9, opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 26 }}
                      className={cn(
                        'text-lg font-semibold',
                        theme === 'dark' ? 'text-white' : 'text-black'
                      )}
                    >
                      {digit}
                    </motion.span>
                  ) : null}
                </AnimatePresence>
              </div>

              {/* Truco: escondemos visualmente el caracter real del input (para que no veas doble) */}
              <style jsx>{`
                input {
                  color: transparent;
                  text-shadow: 0 0 0 ${theme === 'dark' ? 'transparent' : 'transparent'};
                }
                input:focus {
                  caret-color: ${theme === 'dark' ? 'white' : 'black'};
                }
              `}</style>
            </div>
          )
        })}
      </div>
    </div>
  )
}

type Step = 0 | 1 | 2 | 3

function StepDots({ step }: { step: Step }) {
  return (
    <div className="flex items-center gap-1.5">
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={cn(
            'h-1.5 rounded-full transition-all',
            step === i ? 'w-6 bg-foreground/80' : 'w-2 bg-foreground/20'
          )}
        />
      ))}
    </div>
  )
}

export function AuthSignupCard({
  sellerId = 0,
  variant = 'inline',
  chrome = 'glass',
  companyStatus = 0,
  isWhitelabel = false,
  communityName = '',
  communityAvatarUrl = null,
  companyJoinMode = 'open',
  onRequestLogin,
  onSuccess,
  theme = 'dark',
  next = '/',
}: AuthSignupCardProps) {
  const router = useRouter()
  
  const [step, setStep] = useState<Step>(0)

  // step 0: name + email
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')

  const emailNorm = useMemo(() => normalizeEmail(email), [email])
  const [emailError, setEmailError] = useState<string | null>(null)
  const [shakeEmail, setShakeEmail] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [shakePassword, setShakePassword] = useState(false)

  const skipOtpForThisUser = SKIP_OTP_GMAIL_UI && isGmailAddress(emailNorm)

  // step 1: phone + country
  const phoneRef = React.useRef<HTMLInputElement>(null)

  // selector
  const [country, setCountry] = useState<'MX' | 'US' | 'CA' | 'PR' | string>('MX')
  const [phone, setPhone] = useState('') // aquí guardas el valor del input completo: "+52 33..."

  useEffect(() => {
      // solo si está vacío, para no pisar lo que el usuario ya escribió
      if (!phone) setPhone('+52 ')
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function onPhoneInputChange(value: string) {
    // deja que el usuario escriba libre (incluyendo +)
    setPhone(value)

    const e164 = normalizeToE164(value)
    const parsed = parsePhoneNumberFromString(e164)

    if (!parsed) return

    // parsed.country te da 'US', 'CA', 'PR', etc. (cuando aplica)
    if (parsed.country) {
      setCountry(parsed.country)
    }
  }

  function onSelectCountry(iso2: string) {
    setCountry(iso2)
    const calling = byIso[iso2]?.calling || ''

    // conserva los dígitos que el usuario ya escribió DESPUÉS del código actual
    // (simple: nos quedamos con todo lo que no sea prefijo +### al inicio)
    const rest = phone.replace(/^\+\d+\s*/, '') // quita "+52 " o "+1 "
    setPhone(`+${calling} ${rest}`)
  }

  // step 2: password
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')

  // step 3: otp
  const [otpSent, setOtpSent] = useState(false)
  const [code, setCode] = useState('')

  // loading/errors
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const containerClass =
  chrome === 'glass'
    ? cn(
        'w-full rounded-3xl backdrop-blur-xl',
        'bg-white/90 dark:bg-white/5',
        'shadow-[0_20px_60px_rgba(0,0,0,0.10)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.45)]',
        variant === 'inline' ? 'max-w-md p-6' : 'max-w-lg p-5 sm:p-6'
      )
    : 'w-full'

  const canNext0 = name.trim().length >= 2 && !!emailNorm
  const canNext1 = country.trim().length >= 2 && clampDigits(phone, 15).length >= 7
  const canNext2 = password.length >= 8 && password === password2


  const ref = getTrackingFromCookie()
  const key = ref ? `/api/invite?ref=${encodeURIComponent(ref)}` : null

  const { data } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
  })

  const invite = data?.invite || null

  const isInviteOnly = companyJoinMode === 'invite_only'
  const hasInvite = !!invite
  const blockedByInviteOnly = isInviteOnly && !hasInvite

  function checkPassword(p: string) {
    const pass = String(p || '')
    if (pass.length < 8) return { ok: false, msg: 'Mínimo 8 caracteres' }
    if (!/[a-z]/.test(pass)) return { ok: false, msg: 'Incluye una minúscula' }
    if (!/[A-Z]/.test(pass)) return { ok: false, msg: 'Incluye una mayúscula' }
    if (!/\d/.test(pass)) return { ok: false, msg: 'Incluye un número' }
    return { ok: true, msg: '' } 
  }

  async function goNext() {
    setErr(null)

    // ✅ SOLO validar al pasar del Step 1 -> Step 2
    // (ajusta el número si tu step 1 es otro; por tu código parece que 0 es el primero)
    if (step === 0) {
        //const email = String(form.email || '').trim().toLowerCase()

        //console.log('isWhitelabel at submit =', isWhitelabel)

        if (!email) {
        setErr('Ingresa un correo válido')
        return
        }

        const res = await fetch('/api/auth/signup/check-email', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
        if (data?.error === 'EMAIL_EXISTS') {
            setEmailError('Este correo ya está registrado.')
            setShakeEmail(true)

            setTimeout(() => {
            setShakeEmail(false)
            }, 400)

            return
        }
        setErr('No se pudo validar el correo. Intenta de nuevo.')
        return
        }
        setEmailError(null)
    }

    // ✅ Step 2 (ejemplo): validar password ANTES de avanzar
    if (step === 2) {
        const chk = checkPassword(password)

        if (!chk.ok) {
        // tu UI: error + shake + rojo
        setPasswordError(`WEAK PASSWORD: ${chk.msg}`)
        setShakePassword(true)
        setTimeout(() => setShakePassword(false), 400)
        return
        }
    }

    // ✅ si pasó validación (o no aplica), ahora sí avanzas
    setStep((s) => (s < 3 ? ((s + 1) as Step) : s))
  }
  
  function goBack() {
    setErr(null)
    setStep((s) => (s > 0 ? ((s - 1) as Step) : s))
  }

  function handleSignupSuccess() {
    onSuccess?.()

    if (!next) return

    // AIA requiere una navegación completa después del signup.
    // Esto garantiza que las cookies recién creadas sean
    // utilizadas desde la primera petición al servidor.
    if (next === '/aia' || next.startsWith('/aia?')) {
      window.location.replace(next)
      return
    }

    // Mantener intacto el comportamiento del resto de Hubee.
    router.replace(next)
    router.refresh()
  }

  async function startOtp() {
    setErr(null)
    setBusy(true)
    try {
      const res = await fetch('/api/auth/otp/signup/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailNorm,
          // si luego quieres: phone, country
        }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || 'OTP_START_FAILED')
      setOtpSent(true)
      setCode('')
      setStep(3)
    } catch (e: any) {
      setErr(e?.message || 'OTP_START_FAILED')
    } finally {
      setBusy(false)
    }
  }

  async function signupWithoutOtp() {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const locale = navigator.language
    const countryCode = locale.split('-')[1] || 'US'

    setErr(null)
    setBusy(true)
    try {
      const phoneE164 = normalizeToE164(phone)

      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: emailNorm,
          password,

          phone: phoneE164,
          country: country.trim(),

          timezone: String(timezone || '').trim() || 'America/Mexico_City',
          countryId: Number(countryCode || 0) || 0,
          sellerId: Number(sellerId || 0) || 0,
          ref: ref || null,
          whitelabel: isWhitelabel ? 1 : 0,
          companystatus: Number(companyStatus ?? 0),

          // 🔥 override
          skip_otp: 1,
        }),
      })

      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || 'SIGNUP_FAILED')

      handleSignupSuccess()
      return
    } catch (e: any) {
      setErr(e?.message || 'SIGNUP_FAILED')
    } finally {
      setBusy(false)
    }
  }

  async function verifyOtpAndSignup() {    
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const locale = navigator.language
    const countryCode = locale.split('-')[1] || 'US'
    
    setErr(null)
    setBusy(true)
    try {
      // 1) verificar OTP
      const v = await fetch('/api/auth/otp/signup/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNorm, code }),
      })

      const vData = await v.json()
      const otpToken = vData?.otp_token
      if (!otpToken) throw new Error('OTP_VERIFY_FAILED')

      const phoneE164 = normalizeToE164(phone)


      // 2) registrar usuario final
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            name: name.trim(),
            email: emailNorm,
            password,

            phone: phoneE164,
            country: country.trim(),

            // ✅ lo que antes mandabas a verify, va aquí (source of truth del registro)
            timezone: String(timezone || '').trim() || 'America/Mexico_City',
            countryId: Number(countryCode || 0) || 0,
            sellerId: Number(sellerId || 0) || 0,
            ref: ref || null,
            whitelabel: isWhitelabel ? 1 : 0,
            companystatus: Number(companyStatus ?? 0),

            otp_token: otpToken,
        }),
      })

      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || 'SIGNUP_FAILED')

      handleSignupSuccess()
      return
    } catch (e: any) {
      setErr(e?.message || 'SIGNUP_FAILED')
    } finally {
      setBusy(false)
    }
  }

  function isStrongPassword(p: string) {
    if (p.length < 8) return false
    const hasUpper = /[A-Z]/.test(p)
    const hasLower = /[a-z]/.test(p)
    const hasNum = /\d/.test(p)
    return hasUpper && hasLower && hasNum
 }

  // animación simple tipo “slide”
  const trackStyle = {
    transform: `translateX(-${step * 100}%)`,
  } as React.CSSProperties

  if (blockedByInviteOnly) {
    return (
      <div className={containerClass}>
        <div className="text-center">
          {/* Icono premium */}
          <div className="mx-auto relative flex h-16 w-16 items-center justify-center">
            {/* glow suave */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/10 to-white/0 blur-xl" />

            {/* contenedor */}
            <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-background/80 backdrop-blur-md shadow-sm">
              {/* icono */}
              <svg
                className="h-6 w-6 text-foreground/80"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  d="M16 11V7a4 4 0 10-8 0v4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <rect
                  x="5"
                  y="11"
                  width="14"
                  height="10"
                  rx="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h3 className="mt-6 text-xl font-semibold tracking-tight text-foreground">
            Comunidad solo por invitación
          </h3>

          {/* Copy */}
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            Esta comunidad está diseñada para mantener un entorno exclusivo.
            Solo es posible unirse mediante una invitación directa.
          </p>

          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Si ya recibiste una invitación, abre el enlace que te compartieron.
          </p>

          {/* CTA */}
          {onRequestLogin ? (
            <button
              type="button"
              onClick={onRequestLogin}
              className="mt-6 text-sm font-medium text-foreground/80 transition hover:text-foreground"
            >
              Ya tengo cuenta
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className={containerClass}>
      {/* top row */}
      <div className={cn(
                    'flex',
                    (variant === 'modal')
                        ? 'flex-col items-center'
                        : 'items-start justify-between gap-4'
                    )}>
        <div className="min-w-0 w-full">
          {/* 🔹 INVITE (solo si existe) */}
          {(
                invite?.name ? (
                  variant === 'modal' ? (
                    <InviteHeroBadge
                      inviterName={invite.name}
                      inviterAvatarUrl={invite.avatarUrl}
                      communityName={communityName}
                      communityAvatarUrl={communityAvatarUrl}
                      className="mb-6"
                    />
                  ) : (
                    <InviteBadge
                      name={invite.name}
                      avatarUrl={invite.avatarUrl}
                      label="te invitó"
                    />
                  )
                ) : (
                  variant === 'modal' ? (
                    <CommunityWelcomeHero
                      communityName={communityName}
                      communityAvatarUrl={communityAvatarUrl}
                      className="mb-6"
                    />
                  ) : null
                )
          )}
        </div>
        <div className="shrink-0 pt-1">
          <StepDots step={step} />
        </div>
      </div>          

      {/* slider */}
      <div className="mt-5 overflow-hidden">
        <div className="flex w-full transition-transform duration-300 ease-out" style={trackStyle}>
          {/* STEP 0 */}
          <div className="w-full shrink-0 pr-0">

            <Button
              type="button"
              className="h-12 w-full rounded-lg flex items-center justify-center gap-3 bg-white text-black hover:bg-gray-50 border"
              variant="secondary"
              onClick={() => {
                const u = new URL('/api/auth/google/start', window.location.origin)
                u.searchParams.set('next', next)
                u.searchParams.set('sellerId', String(Number(sellerId || 0) || 0))
                u.searchParams.set('approveId', String(Number(companyStatus || 0) || 0))
                u.searchParams.set('whitelabel', isWhitelabel ? '1' : '0')
                u.searchParams.set('joinMode', companyJoinMode || 'open')
                window.location.href = u.toString()
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 48 48"
              >
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.72 1.22 9.23 3.6l6.9-6.9C36.54 2.5 30.7 0 24 0 14.64 0 6.53 5.4 2.56 13.32l8.06 6.26C12.7 13.09 17.9 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.1 24.5c0-1.6-.14-3.14-.4-4.62H24v9h12.44c-.54 2.9-2.2 5.36-4.68 7l7.2 5.6C43.93 37.1 46.1 31.3 46.1 24.5z"/>
                <path fill="#FBBC05" d="M10.62 28.58a14.5 14.5 0 0 1 0-9.16l-8.06-6.26A24.02 24.02 0 0 0 0 24c0 3.87.92 7.54 2.56 10.84l8.06-6.26z"/>
                <path fill="#34A853" d="M24 48c6.7 0 12.34-2.2 16.45-5.98l-7.2-5.6c-2 1.34-4.54 2.13-9.25 2.13-6.1 0-11.3-3.6-13.38-8.78l-8.06 6.26C6.53 42.6 14.64 48 24 48z"/>
              </svg>

              Continuar con Google
            </Button>

          <div className="my-4 flex w-full items-center justify-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-black/10 dark:to-white/10" />

            <span className="shrink-0 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              o
            </span>

            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-black/10 dark:to-white/10" />
          </div>  

            <div className="space-y-4">
              <div className="space-y-2">
                <div className="text-xs font-medium text-foreground/80 hidden">Nombre</div>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre"
                  className="h-12 rounded-lg bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/10"
                />
              </div>

              <div className="space-y-2">
                <div className="text-xs font-medium text-foreground/80 hidden">Email</div>

                <Input
                    value={email}
                    onChange={(e) => {
                    setEmail(e.target.value)
                    setEmailError(null)
                    }}
                    placeholder="Correo Electrónico"
                    className={cn(
                    'h-12 rounded-lg transition-all duration-200',
                    'bg-white/60 dark:bg-white/5',
                    emailError
                        ? 'border border-red-500 focus:ring-2 focus:ring-red-500/40'
                        : 'border border-black/5 dark:border-white/10',
                    shakeEmail && 'animate-shake'
                    )}
                />

                {emailError && (
                    <div className="text-xs text-red-500">{emailError}</div>
                )}
                </div>
            </div>
          </div>

          {/* STEP 1 */}
          <div className="w-full shrink-0 pl-4 pr-0">
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                {/* selector */}
                <div className="space-y-2 col-span-1">
                  <div className="text-xs font-medium text-foreground/80">País</div>

                  <select
                    value={country}
                    onChange={(e) => onSelectCountry(e.target.value)}
                    className="h-12 w-full rounded-lg bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/10 px-3"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.iso2} value={c.iso2}>
                        {c.iso2} (+{c.calling})
                      </option>
                    ))}
                  </select>
                </div>

                {/* input */}
                <div className="space-y-2 col-span-2">
                  <div className="text-xs font-medium text-foreground/80">Teléfono</div>

                  <input
                    ref={phoneRef}
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => onPhoneInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      // evita borrar el "+"
                      if (e.key === 'Backspace') {
                        const el = e.currentTarget
                        const pos = el.selectionStart || 0
                        if (pos <= 1) e.preventDefault()
                      }
                    }}
                    placeholder="+52 55 1234 5678"
                    className={cn(
                      'h-12 w-full rounded-lg px-4 text-sm outline-none transition',
                      'bg-white/60 dark:bg-white/5',
                      'border border-black/5 dark:border-white/10',
                      'focus:ring-2 focus:ring-black/10 dark:focus:ring-white/15'
                    )}
                  />
                </div>
              </div>

              <div className="text-xs text-muted-foreground">
                Te mandaremos un código para confirmar tu email. (Teléfono se usa para seguridad y futuras alertas.)
              </div>
            </div>
          </div>

          {/* STEP 2 */}
          <div className="w-full shrink-0 pl-4 pr-0">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="text-xs font-medium text-foreground/80">Contraseña</div>

              <Input
                    type="password"
                    value={password}
                    onChange={(e) => {
                    setPassword(e.target.value)
                    setPasswordError(null)
                    }}
                    placeholder="mínimo 8 caracteres"
                    className={cn(
                    'h-12 rounded-lg transition-all duration-200',
                    'bg-white/60 dark:bg-white/5',
                    passwordError
                        ? 'border border-red-500 focus:ring-2 focus:ring-red-500/40'
                        : 'border border-black/5 dark:border-white/10',
                    shakePassword && 'animate-shake'
                    )}
                />
              </div>

              <div className="space-y-2">
                <div className="text-xs font-medium text-foreground/80">Confirmar</div>
                
              <Input
                    type="password"
                    value={password2}
                    onChange={(e) => {
                    setPassword2(e.target.value)
                    setPasswordError(null)
                    }}
                    placeholder="repite tu contraseña"
                    className={cn(
                    'h-12 rounded-lg transition-all duration-200',
                    'bg-white/60 dark:bg-white/5',
                    passwordError
                        ? 'border border-red-500 focus:ring-2 focus:ring-red-500/40'
                        : 'border border-black/5 dark:border-white/10',
                    shakePassword && 'animate-shake'
                    )}
                />
              </div>

                {passwordError && (
                    <div className="text-xs text-red-500">{passwordError}</div>
                )}

              <div className="text-xs text-muted-foreground">
                Tip: usa una frase corta y fácil de recordar.
              </div>
            </div>
          </div>

          {/* STEP 3 */}
          <div className="w-full shrink-0 pl-4 pr-0">
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Confirma tu email con el código de 6 dígitos.
              </div>

              {skipOtpForThisUser ? (
                <div className="space-y-3">
                  <div className="text-sm text-muted-foreground">
                    Por el momento, Gmail está presentando retrasos con el código. Continuaremos sin OTP.
                  </div>

                  {err ? <div className="text-sm text-destructive">{err}</div> : null}

                  <Button
                    className="h-12 w-full rounded-lg"
                    disabled={busy}
                    onClick={signupWithoutOtp}
                  >
                    {busy ? 'Creando…' : 'Crear cuenta'}
                  </Button>
                </div>
              ) : (
                <>
                  
              {/* tu UI actual de OTP */}
              <OtpBoxes
                value={code}
                onChange={(v) => setCode(clampDigits(v, 6))}
                disabled={busy}
                theme={theme}
              />

              {err ? <div className="text-sm text-destructive">{err}</div> : null}

              {!otpSent ? (
                <Button
                  className="h-12 w-full rounded-lg"
                  disabled={busy || !emailNorm}
                  onClick={startOtp}
                >
                  {busy ? 'Enviando…' : 'Enviar código'}
                </Button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="secondary"
                    className="h-12 rounded-lg"
                    disabled={busy}
                    onClick={startOtp}
                  >
                    {busy ? 'Enviando…' : 'Reenviar'}
                  </Button>
                  <Button
                    className="h-12 rounded-lg"
                    disabled={busy || code.length !== 6}
                    onClick={verifyOtpAndSignup}
                  >
                    {busy ? 'Creando…' : 'Crear cuenta'}
                  </Button>
                </div>
              )}
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* footer actions */}
      {err && step !== 3 ? <div className="mt-3 text-sm text-destructive">{err}</div> : null}

      <div className="mt-5 flex items-center justify-between gap-2">
        <Button
          variant="secondary"
          className="h-11 rounded-2xl"
          disabled={busy || step === 0}
          onClick={goBack}
        >
          Atrás
        </Button>

        {step < 3 ? (
          <Button
            className="h-11 rounded-2xl"
            disabled={
              busy ||
              (step === 0 && !canNext0) ||
              (step === 1 && !canNext1) ||
              (step === 2 && !canNext2)
            }
            onClick={() => {
              setErr(null)
              if (step === 2) {
                if (skipOtpForThisUser) {
                  // ✅ no mostramos OTP, registramos directo
                  signupWithoutOtp()
                  return
                }

                // flujo normal (OTP)
                setStep(3)
                setOtpSent(false)
                setCode('')
                return
              }

              goNext()
            }}
          >
            Siguiente
          </Button>
        ) : (
          <div className="flex-1" />
        )}
      </div>
      <p className="mt-4 text-center text-xs leading-5 text-zinc-500">
        Al continuar, aceptas los{' '}
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-zinc-800 underline decoration-zinc-300 underline-offset-4 transition hover:text-black hover:decoration-black dark:text-zinc-100 dark:decoration-zinc-600 dark:hover:text-white dark:hover:decoration-white"
        >
          Términos y Condiciones
        </a>{' '}
        y confirmas haber leído la{' '}
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-zinc-800 underline decoration-zinc-300 underline-offset-4 transition hover:text-black hover:decoration-black dark:text-zinc-100 dark:decoration-zinc-600 dark:hover:text-white dark:hover:decoration-white"
        >
          Política de Privacidad
        </a>
        .
      </p>
    </div>
  )
}
